import chalk from "chalk";
import * as fs from "fs";
import * as readline from "readline";
import {
  CrunchServiceWithContext,
  ModelNotFoundError,
  NoModelsRegisteredError,
  CrunchAccountServiceWithContext,
  formatMicroUsdcToUsdc,
} from "@crunchdao/sdk";
import { readPrizeJsonFile, formatAddressWithLink } from "@crunchdao/shared-cli";
import { createTable } from "@crunchdao/shared-cli";
import { EnhancedBaseCommand } from "@crunchdao/shared-cli";

const MAX_PRIZES_IN_PREVIEW = 5;

export class CheckpointCommand extends EnhancedBaseCommand {
  private async promptUserConfirmation(message: string): Promise<boolean> {
    const rl = readline.createInterface({
      input: process.stdin,
      output: process.stdout,
    });

    return new Promise((resolve) => {
      rl.question(`${message} (y/N): `, (answer) => {
        rl.close();
        resolve(answer.toLowerCase() === "y" || answer.toLowerCase() === "yes");
      });
    });
  }

  private async displayPreparedPrizesPreview(
    crunchName: string,
    preparedPrizes: any[],
    originalPrizes: any[]
  ): Promise<void> {
    console.log(chalk.cyan.bold(`\nPrepared Prizes Preview for: ${crunchName}`));
    console.log(chalk.gray("═".repeat(80)));

    const previewTable = createTable({ colWidths: [5, 15, 12, 18, 25] });
    previewTable.push([
      chalk.yellow("#"),
      chalk.yellow("Amount (USDC)"),
      chalk.yellow("Cruncher Idx"),
      chalk.yellow("Cruncher Name"),
      chalk.yellow("Status"),
    ]);

    let totalPayout = 0;
    const prizesToShow = Math.min(preparedPrizes.length, MAX_PRIZES_IN_PREVIEW);

    for (let i = 0; i < prizesToShow; i++) {
      const prize = preparedPrizes[i];
      totalPayout += prize.prize;

      previewTable.push([
        chalk.white((i + 1).toString()),
        chalk.green(formatMicroUsdcToUsdc(prize.prize, 6, false)),
        chalk.white(prize.cruncherIndex.toString()),
        chalk.cyan(
          prize.cruncherName.length > 16
            ? prize.cruncherName.slice(0, 16) + ".."
            : prize.cruncherName
        ),
        chalk.green("✓ Ready"),
      ]);
    }

    if (preparedPrizes.length > MAX_PRIZES_IN_PREVIEW) {
      const remainingPayout = preparedPrizes
        .slice(MAX_PRIZES_IN_PREVIEW)
        .reduce((sum, p) => sum + p.prize, 0);
      previewTable.push([
        chalk.gray("..."),
        chalk.gray(`+${formatMicroUsdcToUsdc(remainingPayout, 6, false)}`),
        "",
        chalk.gray(`+${preparedPrizes.length - MAX_PRIZES_IN_PREVIEW} more`),
        "",
      ]);
      totalPayout = preparedPrizes.reduce((sum, p) => sum + p.prize, 0);
    }

    console.log(previewTable.toString());

    // Enhanced Summary
    console.log(chalk.cyan.bold("\nPrepared Prizes Summary"));
    console.log(chalk.gray("─".repeat(50)));
    const summaryTable = createTable({ colWidths: [25, 30] });

    const uniqueCrunchers = new Set(preparedPrizes.map((p) => p.cruncherAddress.toString())).size;
    const groupingReduction = originalPrizes.length - preparedPrizes.length;

    summaryTable.push(
      [chalk.yellow("Original Prizes"), chalk.white(originalPrizes.length.toString())],
      [chalk.yellow("Prepared Prizes"), chalk.white(preparedPrizes.length.toString())],
      [chalk.yellow("Unique Crunchers"), chalk.white(uniqueCrunchers.toString())],
      [
        chalk.yellow("Grouping Reduction"),
        groupingReduction > 0 ? chalk.green(`-${groupingReduction}`) : chalk.gray("0"),
      ],
      [chalk.yellow("Total Payout"), chalk.green(formatMicroUsdcToUsdc(totalPayout, 6))],
      [chalk.yellow("Token Accounts"), chalk.green("✓ Created")]
    );
    console.log(summaryTable.toString());

    console.log(chalk.gray("═".repeat(80)));
  }

  private displayCheckpointInfo(data: any, crunchName: string, isCurrent: boolean = false) {
    // Header with checkpoint title
    const title = isCurrent
      ? `Current Checkpoint for: ${crunchName}`
      : `Checkpoint #${data.account.index} for: ${crunchName}`;
    console.log(chalk.cyan.bold(`\n${title}`));
    console.log(chalk.gray("═".repeat(80)));

    // Main checkpoint table
    const mainTable = createTable({ colWidths: [25, 55] });

    mainTable.push(
      [chalk.yellow("Address"), formatAddressWithLink(data.address, this.network)],
      [chalk.yellow("Index"), chalk.white(data.account.index.toString())],
      [chalk.yellow("Status"), this.formatCheckpointStatus(data.account.status)],
      [chalk.yellow("Created"), chalk.white(data.account.timestampDate)],
      [chalk.yellow("Timestamp"), chalk.gray(data.account.timestamp.toString())],
      [chalk.yellow("Total Prizes"), chalk.green(data.account.prizes.length.toString())],
      [chalk.yellow("Crunch"), formatAddressWithLink(data.account.crunch, this.network)],
      [chalk.yellow("Coordinator"), formatAddressWithLink(data.account.coordinator, this.network)]
    );

    console.log(mainTable.toString());

    // Prizes section
    if (data.account.prizes.length > 0) {
      console.log(chalk.cyan.bold("\nPrizes"));
      console.log(chalk.gray("─".repeat(60)));

      const prizesTable = createTable({ colWidths: [5, 15, 15, 10] });
      prizesTable.push([
        chalk.yellow("#"),
        chalk.yellow("Amount"),
        chalk.yellow("Cruncher Index"),
        chalk.yellow("Claimed"),
      ]);

      // Show up to 10 prizes
      const prizesToShow = Math.min(data.account.prizes.length, 10);
      for (let i = 0; i < prizesToShow; i++) {
        const prize = data.account.prizes[i];
        prizesTable.push([
          chalk.white((i + 1).toString()),
          chalk.green(formatMicroUsdcToUsdc(prize.prize)),
          chalk.white(prize.cruncherIndex.toString()),
          prize.claimed ? chalk.green("✓ Yes") : chalk.red("✗ No"),
        ]);
      }

      if (data.account.prizes.length > 10) {
        prizesTable.push([
          chalk.gray("..."),
          chalk.gray(`and ${data.account.prizes.length - 10} more`),
          "",
          "",
        ]);
      }

      console.log(prizesTable.toString());

      // Summary stats
      const totalAmount = data.account.prizes.reduce(
        (sum: number, prize: any) => sum + prize.prize,
        0
      );
      const claimedCount = data.account.prizes.filter((prize: any) => prize.claimed).length;

      console.log(chalk.cyan.bold("\nSummary"));
      console.log(chalk.gray("─".repeat(40)));

      const summaryTable = createTable({ colWidths: [20, 30] });
      summaryTable.push(
        [chalk.yellow("Total Amount"), chalk.green(formatMicroUsdcToUsdc(totalAmount))],
        [chalk.yellow("Claimed"), chalk.white(`${claimedCount}/${data.account.prizes.length}`)],
        [chalk.yellow("Remaining"), chalk.white(`${data.account.prizes.length - claimedCount}`)]
      );

      console.log(summaryTable.toString());
    }

    console.log(chalk.gray("\n" + "═".repeat(80)));
  }

  private formatCheckpointStatus(status: string): string {
    switch (status.toLowerCase()) {
      case "loadedprizes":
        return chalk.green("🟢 Loaded Prizes");
      case "processing":
        return chalk.yellow("🟡 Processing");
      case "finished":
        return chalk.blue("🔵 Finished");
      default:
        return chalk.white(`⚪ ${status}`);
    }
  }
  async create(crunchName: string, prizeFileName: string, dryrun: boolean = false): Promise<void> {
    try {
      const crunchService = CrunchServiceWithContext({
        program: this.program,
      });

      // Check if prize file exists
      if (!prizeFileName) {
        const customError = this.createEnhancedError(
          "Prize file name is required",
          "PrizeFileRequiredError",
          "PRIZE_FILE_REQUIRED",
          {
            crunchName,
            suggestion: "Provide a valid prize file path as argument",
          }
        );
        return this.handleError(customError);
      }

      if (!fs.existsSync(prizeFileName)) {
        const customError = this.createEnhancedError(
          `Prize file "${prizeFileName}" does not exist`,
          "PrizeFileNotFoundError",
          "PRIZE_FILE_NOT_FOUND",
          {
            crunchName,
            prizeFileName,
            suggestion: "Check the file path and ensure the prize file exists",
          }
        );
        return this.handleError(customError);
      }

      let prizes: any[];
      try {
        prizes = readPrizeJsonFile(prizeFileName);
      } catch (error) {
        // Type guard: error is unknown, so we need to check its shape
        if (
          typeof error === "object" &&
          error !== null &&
          "message" in error &&
          typeof (error as { message: string }).message === "string" &&
          ((error as { message: string }).message.includes("Invalid JSON") ||
            (error as { message: string }).message.includes("Invalid prize format"))
        ) {
          const errMsg = (error as { message: string }).message;
          const customError = this.createEnhancedError(
            `Prize file validation failed: ${errMsg}`,
            "InvalidPrizeFileError",
            "INVALID_PRIZE_FILE_FORMAT",
            {
              prizeFileName,
              validationError: errMsg,
              expectedFormat:
                'Each line should contain: {"timestamp": number, "model": "string", "prize": number}. prizeId is optional.',
              suggestion:
                "Check the prize file format and ensure all required fields are present with correct types",
            },
            error
          );
          return this.handleError(customError);
        }
        throw error; // Re-throw if it's a different type of error
      }

      const prizesToPayout = prizes;

      // Prepare prizes early - this allows inspection and reuse
      console.log(chalk.cyan("🔄 Preparing prizes..."));
      const preparedPrizes = await crunchService.checkpointPreparePrizes(
        crunchName,
        prizesToPayout
      );
      console.log(chalk.green(`✅ Prepared ${preparedPrizes.length} prizes`));

      // Show enhanced preview with prepared prize info
      if (!this.resolvedOptions.quiet && !dryrun) {
        await this.displayPreparedPrizesPreview(crunchName, preparedPrizes, prizesToPayout);

        const confirmed = await this.promptUserConfirmation(
          chalk.yellow(
            `\nDo you want to create this checkpoint with ${preparedPrizes.length} prepared prizes?`
          )
        );

        if (!confirmed) {
          console.log(chalk.gray("\nCheckpoint creation cancelled by user."));
          return;
        }
      }

      // Execute checkpoint creation with prepared prizes
      // Create detailed memo with prize breakdown
      const totalPayout = preparedPrizes.reduce((sum, prize) => sum + prize.prize, 0);
      const prizeList = preparedPrizes
        .slice(0, 10)
        .map(
          (prize, index) =>
            `${index + 1}. ${prize.cruncherName}: ${formatMicroUsdcToUsdc(prize.prize, 2)}`
        )
        .join("\n");

      const remainingPrizes =
        preparedPrizes.length > 10 ? `\n... and ${preparedPrizes.length - 10} more prizes` : "";

      const detailedMemo = `Create checkpoint for "${crunchName}" with ${
        preparedPrizes.length
      } prizes (${formatMicroUsdcToUsdc(totalPayout, 2)} total):\n${prizeList}${remainingPrizes}`;

      const output = await this.executeOrProposeInstructions(
        async () => {
          return await crunchService.checkpointCreate(crunchName, preparedPrizes, this.getSigner());
        },
        detailedMemo,
        { useMultisigBatch: true }
      );

      if (this.resolvedOptions.output === "json") {
        // This is still a work in progress
        this.output(output);
        this.success("Checkpoint created successfully", {
          crunchName,
          prizeFileName,
          prizesProcessed: prizesToPayout.length,
          totalPrizes: prizes.length,
          checkpointOutput: output,
        });
      } else {
        this.displayActionComplete("Checkpoint created successfully", this.isMultisigMode());
      }
    } catch (error) {
      // Check for NoModelsRegisteredError first
      if (error instanceof NoModelsRegisteredError) {
        const customError = this.createEnhancedError(
          `No models registered for crunch "${crunchName}". At least one model must be registered before creating checkpoints.`,
          "NoModelsRegisteredForCrunchError",
          "NO_MODELS_REGISTERED_FOR_CRUNCH",
          {
            crunchName,
            prizeFileName,
          },
          error
        );

        return this.handleError(customError);
      }

      // Check for ModelNotFoundError second
      if (error instanceof ModelNotFoundError) {
        const customError = this.createEnhancedError(
          `Model "${error.modelId}" does not exist in crunch "${crunchName}".`,
          "ModelNotFoundInPrizeFileError",
          "MODEL_NOT_FOUND_IN_PRIZE_FILE",
          {
            crunchName,
            modelId: error.modelId,
            prizeFileName,
          },
          error
        );

        return this.handleError(customError);
      }

      this.handleTransactionError(error, crunchName);
    }
  }

  private transformCheckpointData(checkpoint: any) {
    return {
      address: checkpoint.address.toBase58(),
      account: {
        status: Object.keys(checkpoint.account.status)[0], // Extract the status key
        crunch: checkpoint.account.crunch.toBase58(),
        coordinator: checkpoint.account.coordinator.toBase58(),
        index: checkpoint.account.index,
        timestamp: Number(checkpoint.account.timestamp), // Convert BigNum to number
        timestampDate: new Date(Number(checkpoint.account.timestamp) * 1000).toISOString(), // Human readable date
        prizes: checkpoint.account.prizes.map((prize: any) => ({
          prize: Number(prize.prize), // Convert BigNum to number
          cruncherIndex: prize.cruncherIndex,
          claimed: prize.claimed,
        })),
        // Intentionally omitting bump field as requested
      },
    };
  }

  async getCurrent(crunchName: string): Promise<void> {
    try {
      const crunchAccountService = CrunchAccountServiceWithContext({
        program: this.program,
      });

      const crunchAddress = crunchAccountService.getCrunchAddress(crunchName);
      const checkpoint = await crunchAccountService.getCurrentCheckpoint(crunchAddress);

      if (!checkpoint) {
        const customError = this.createEnhancedError(
          `No current checkpoint found for crunch "${crunchName}"`,
          "NoCurrentCheckpointError",
          "NO_CURRENT_CHECKPOINT",
          {
            crunchName,
            suggestion:
              "Create a checkpoint first, see https://protocol.crunchdao.com/cli/coordinator#checkpoint-create",
          }
        );
        return this.handleError(customError);
      }

      // Transform the checkpoint data to be human-readable
      const transformedCheckpoint = this.transformCheckpointData(checkpoint);

      // Output based on format preference
      if (this.resolvedOptions.output === "json") {
        this.output(transformedCheckpoint);
      } else {
        // Create beautiful table output
        this.displayCheckpointInfo(transformedCheckpoint, crunchName, true);
      }
    } catch (error) {
      this.handleTransactionError(error, crunchName);
    }
  }

  async getByIndex(crunchName: string, checkpointIndex: string): Promise<void> {
    try {
      const crunchAccountService = CrunchAccountServiceWithContext({
        program: this.program,
      });

      const crunchAddress = crunchAccountService.getCrunchAddress(crunchName);
      const checkpointAddress = crunchAccountService.getCheckpointAddress(
        crunchAddress,
        Number(checkpointIndex)
      );

      // Get the checkpoint account
      const checkpointAccount = await this.program.account.checkpoint.fetch(checkpointAddress);

      if (!checkpointAccount) {
        const customError = this.createEnhancedError(
          `Checkpoint #${checkpointIndex} not found for crunch "${crunchName}"`,
          "CheckpointNotFoundError",
          "CHECKPOINT_NOT_FOUND",
          {
            crunchName,
            checkpointIndex: Number(checkpointIndex),
            checkpointAddress: checkpointAddress.toBase58(),
            suggestion: "Check if the checkpoint index is correct or if the checkpoint exists",
          }
        );
        return this.handleError(customError);
      }

      // Create the same structure as getCurrentCheckpoint returns
      const checkpoint = {
        address: checkpointAddress,
        account: checkpointAccount,
      };

      // Transform the checkpoint data to be human-readable
      const transformedCheckpoint = this.transformCheckpointData(checkpoint);

      // Output based on format preference
      if (this.resolvedOptions.output === "json") {
        this.output(transformedCheckpoint);
      } else {
        // Create beautiful table output
        this.displayCheckpointInfo(transformedCheckpoint, crunchName, false);
      }
    } catch (error) {
      this.handleTransactionError(error, crunchName);
    }
  }

  async getAddress(crunchName: string, checkpointIndex: string): Promise<void> {
    try {
      const crunchAccountService = CrunchAccountServiceWithContext({
        program: this.program,
      });

      const crunchAddress = crunchAccountService.getCrunchAddress(crunchName);
      const checkpointAddress = crunchAccountService.getCheckpointAddress(
        crunchAddress,
        Number(checkpointIndex)
      );

      this.success("Checkpoint address retrieved", {
        crunchName,
        checkpointIndex: Number(checkpointIndex),
        checkpointAddress: checkpointAddress.toBase58(),
      });
    } catch (error) {
      this.handleTransactionError(error, crunchName);
    }
  }
}
